import { useMemo, useState } from 'preact/hooks';
import type { JSX } from 'preact';

export interface ChartPoint {
  label: string;
  value: number;
}

interface BaseProps {
  points: ChartPoint[];
  height?: number;
  /** e.g. (v) => `${v}%` */
  formatValue?: (v: number) => string;
  color?: string;
  title?: string;
}

const PAD = { top: 10, right: 10, bottom: 22, left: 10 };

function useHover(points: ChartPoint[], width: number) {
  const [hover, setHover] = useState<number | null>(null);
  const innerW = width - PAD.left - PAD.right;
  const step = points.length > 1 ? innerW / (points.length - 1) : 0;
  const onMove = (e: JSX.TargetedMouseEvent<SVGSVGElement>) => {
    const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
    const x = e.clientX - rect.left;
    const scale = width / rect.width;
    const localX = x * scale - PAD.left;
    const idx = points.length > 1 ? Math.round(localX / step) : 0;
    setHover(Math.min(Math.max(idx, 0), points.length - 1));
  };
  const onLeave = () => setHover(null);
  return { hover, onMove, onLeave, innerW, step };
}

/** Single-series line chart: one hue, thin 2px stroke, rounded data-ends, recessive grid. */
export function LineChart({
  points,
  height = 140,
  formatValue,
  color = 'var(--amber)',
  title,
}: BaseProps) {
  const width = 480;
  const innerH = height - PAD.top - PAD.bottom;
  const fmt = formatValue ?? ((v: number) => String(v));
  const max = Math.max(1, ...points.map((p) => p.value));
  const { hover, onMove, onLeave, step } = useHover(points, width);

  const coords = useMemo(
    () =>
      points.map((p, i) => ({
        x: PAD.left + i * step,
        y: PAD.top + innerH - (p.value / max) * innerH,
        p,
      })),
    [points, step, innerH, max],
  );

  if (points.length === 0) return <div class="empty">No data</div>;

  const path = coords
    .map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(1)},${c.y.toFixed(1)}`)
    .join(' ');
  const area = `${path} L${coords[coords.length - 1]!.x.toFixed(1)},${PAD.top + innerH} L${coords[0]!.x.toFixed(1)},${PAD.top + innerH} Z`;
  const hovered = hover !== null ? coords[hover] : undefined;
  const labelEvery = Math.max(1, Math.ceil(points.length / 6));

  return (
    <div style={{ position: 'relative' }}>
      {title && (
        <div class="section-label" style={{ margin: '0 0 6px' }}>
          {title}
        </div>
      )}
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        role="img"
        aria-label={title}
      >
        <line
          x1={PAD.left}
          y1={PAD.top + innerH}
          x2={width - PAD.right}
          y2={PAD.top + innerH}
          stroke="var(--border)"
          stroke-width="1"
        />
        <path d={area} fill={color} opacity="0.12" stroke="none" />
        <path
          d={path}
          fill="none"
          stroke={color}
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        {coords.map(
          (c, i) =>
            i % labelEvery === 0 && (
              <text
                key={i}
                x={c.x}
                y={height - 6}
                font-size="9"
                fill="var(--bone-faint)"
                text-anchor="middle"
              >
                {c.p.label}
              </text>
            ),
        )}
        {hovered && (
          <>
            <line
              x1={hovered.x}
              y1={PAD.top}
              x2={hovered.x}
              y2={PAD.top + innerH}
              stroke="var(--bone-faint)"
              stroke-width="1"
              stroke-dasharray="2,3"
            />
            <circle
              cx={hovered.x}
              cy={hovered.y}
              r="4"
              fill={color}
              stroke="var(--ink)"
              stroke-width="1.5"
            />
          </>
        )}
      </svg>
      {hovered && (
        <div
          style={{
            position: 'absolute',
            left: `${(hovered.x / width) * 100}%`,
            top: 0,
            transform: 'translate(-50%, -100%)',
            background: 'var(--char)',
            border: '1px solid var(--border)',
            borderRadius: '4px',
            padding: '3px 7px',
            fontSize: '11px',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          <b style={{ color }}>{fmt(hovered.p.value)}</b> · {hovered.p.label}
        </div>
      )}
    </div>
  );
}

/** Single-series bar chart: thin bars, 2px surface gap, rounded ends. */
export function BarChart({
  points,
  height = 140,
  formatValue,
  color = 'var(--amber)',
  title,
}: BaseProps) {
  const width = 480;
  const innerH = height - PAD.top - PAD.bottom;
  const fmt = formatValue ?? ((v: number) => String(v));
  const max = Math.max(1, ...points.map((p) => p.value));
  const { hover, onMove, onLeave, step, innerW } = useHover(points, width);
  const barW = points.length > 0 ? Math.max(3, Math.min(28, innerW / points.length - 4)) : 0;
  const labelEvery = Math.max(1, Math.ceil(points.length / 6));

  if (points.length === 0) return <div class="empty">No data</div>;

  return (
    <div style={{ position: 'relative' }}>
      {title && (
        <div class="section-label" style={{ margin: '0 0 6px' }}>
          {title}
        </div>
      )}
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        role="img"
        aria-label={title}
      >
        <line
          x1={PAD.left}
          y1={PAD.top + innerH}
          x2={width - PAD.right}
          y2={PAD.top + innerH}
          stroke="var(--border)"
          stroke-width="1"
        />
        {points.map((p, i) => {
          const cx = PAD.left + i * step;
          const h = (p.value / max) * innerH;
          const isHover = hover === i;
          return (
            <g key={i}>
              <rect
                x={cx - barW / 2}
                y={PAD.top + innerH - h}
                width={barW}
                height={Math.max(0, h)}
                rx="2"
                fill={color}
                opacity={isHover ? 1 : 0.75}
              />
              {i % labelEvery === 0 && (
                <text
                  x={cx}
                  y={height - 6}
                  font-size="9"
                  fill="var(--bone-faint)"
                  text-anchor="middle"
                >
                  {p.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {hover !== null && points[hover] && (
        <div
          style={{
            position: 'absolute',
            left: `${((PAD.left + hover * step) / width) * 100}%`,
            top: 0,
            transform: 'translate(-50%, -100%)',
            background: 'var(--char)',
            border: '1px solid var(--border)',
            borderRadius: '4px',
            padding: '3px 7px',
            fontSize: '11px',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          <b style={{ color }}>{fmt(points[hover]!.value)}</b> · {points[hover]!.label}
        </div>
      )}
    </div>
  );
}

export interface DonutSlice {
  label: string;
  value: number;
  color: string;
}

/** Hand-rolled donut (stacked-arc technique: one full-circle track + one stroked circle per
 * slice, each rotated into place via `stroke-dashoffset`). Total in the centre, swatch legend
 * beside it — no hover, matches the recessive-grid, single-hue-per-series look of the line/bar charts. */
export function DonutChart({
  data,
  size = 132,
  thickness = 20,
  title,
}: {
  data: DonutSlice[];
  size?: number;
  thickness?: number;
  title?: string;
}) {
  const total = data.reduce((sum, d) => sum + Math.max(0, d.value), 0);
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;

  if (total === 0) {
    return (
      <div>
        {title && (
          <div class="section-label" style={{ margin: '0 0 6px' }}>
            {title}
          </div>
        )}
        <div class="empty">No data</div>
      </div>
    );
  }

  let offset = 0;
  const arcs = data
    .filter((d) => d.value > 0)
    .map((d) => {
      const dash = (d.value / total) * c;
      const arc = { ...d, dash, offset };
      offset += dash;
      return arc;
    });

  return (
    <div>
      {title && (
        <div class="section-label" style={{ margin: '0 0 6px' }}>
          {title}
        </div>
      )}
      <div class="row" style={{ alignItems: 'center', gap: 18, flexWrap: 'nowrap' }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          role="img"
          aria-label={title}
          style={{ flex: 'none' }}
        >
          <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke="var(--char-2)"
              stroke-width={thickness}
            />
            {arcs.map((a, i) => (
              <circle
                key={i}
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={a.color}
                stroke-width={thickness}
                stroke-dasharray={`${a.dash.toFixed(1)} ${(c - a.dash).toFixed(1)}`}
                stroke-dashoffset={(-a.offset).toFixed(1)}
              />
            ))}
          </g>
          <text
            x={size / 2}
            y={size / 2}
            text-anchor="middle"
            dominant-baseline="central"
            font-size="18"
            fill="var(--bone)"
          >
            {total}
          </text>
        </svg>
        <div class="legend" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
          {data.map((d) => (
            <span key={d.label}>
              <span class="swatch" style={{ background: d.color }} />
              {d.label} · {d.value} ({Math.round((Math.max(0, d.value) / total) * 100)}%)
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Tiny inline trend line for stat tiles — no axes, no legend, no hover. */
export function Sparkline({
  values,
  color = 'var(--amber)',
}: {
  values: number[];
  color?: string;
}) {
  const width = 120;
  const height = 32;
  if (values.length < 2) return null;
  const max = Math.max(1, ...values);
  const min = Math.min(0, ...values);
  const range = max - min || 1;
  const step = width / (values.length - 1);
  const path = values
    .map(
      (v, i) =>
        `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${(height - ((v - min) / range) * height).toFixed(1)}`,
    )
    .join(' ');
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} aria-hidden="true">
      <path
        d={path}
        fill="none"
        stroke={color}
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}
